import {
  dealTypeFromJsonQuery,
  FDealType,
  FOfferType,
  isCommercial,
  isSuburban,
  isUrban,
  offerTypeFromJsonQuery,
  TJsonQuery,
} from '../../../packages/JsonQuery';

/** Значение, когда тип не раскладывается в перечисленные в SLO корзины */
const UNKNOWN = 'unknown';

/**
 * Тип сделки в терминах SLO: buy, rent или daily_rent.
 *
 * Посутку отличаем не по `_type` (у неё тот же `flatrent`), а по терму `for_day` —
 * это уже умеет dealTypeFromJsonQuery.
 */
function getDealTypeKey(jsonQuery: TJsonQuery): string {
  const dealType = dealTypeFromJsonQuery(jsonQuery);

  if (dealType === FDealType.Sale) {
    return 'buy';
  }

  if (dealType === FDealType.RentDaily) {
    return 'daily_rent';
  }

  /** Аренда без уточнения срока и длительная попадают в одну корзину */
  if ((dealType & FDealType.Rent) !== 0) {
    return 'rent';
  }

  return UNKNOWN;
}

/**
 * Тип недвижимости в терминах SLO: resale, new_building, mixed, commercial или suburban.
 *
 * Новостройки и вторичка приезжают из `building_status` уже разложенными по битам
 * FlatNew и FlatOld: оба бита сразу означают выдачу без фильтра по типу дома, то есть mixed.
 */
function getOfferTypeKey(jsonQuery: TJsonQuery): string {
  const offerType = offerTypeFromJsonQuery(jsonQuery);

  if (isCommercial(offerType)) {
    return 'commercial';
  }

  if (isSuburban(offerType)) {
    return 'suburban';
  }

  if (isUrban(offerType)) {
    const isNewBuilding = (offerType & FOfferType.FlatNew) !== 0;
    const isResale = (offerType & FOfferType.FlatOld) !== 0;

    if (isNewBuilding && isResale) {
      return 'mixed';
    }

    if (isNewBuilding) {
      return 'new_building';
    }

    /** Комнаты, доли и койко-места новостройками не бывают */
    return 'resale';
  }

  return UNKNOWN;
}

/**
 * Ключ телеметрии загрузки выдачи в разрезе вертикалей.
 *
 * У statsd-счётчиков нет полей, поэтому размерности из SLO — тип сделки и тип недвижимости —
 * уносим в суффикс ключа. Доля нулевых выдач считается как отношение `empty` к `shown`
 * с одинаковым суффиксом.
 */
export function getListingTelemetryKey(jsonQuery: TJsonQuery, isEmpty: boolean): string {
  const state = isEmpty ? 'empty' : 'shown';

  return `serp.listing.${state}.${getDealTypeKey(jsonQuery)}.${getOfferTypeKey(jsonQuery)}`;
}
